import apiInstance from 'src/apis/config/axiosConfig';

const base = '/acs-integrations';

export interface AcsPdkConfigJson {
  systemId?: string;
  holderAutoSyncEnabled?: boolean;
  autoSyncBuildingId?: string;
  autoSyncBuildingName?: string;
  autoSyncApartmentId?: string;
  autoSyncApartmentName?: string;
  pdkOuId?: string;
  webhookSubscriptionId?: string;
  webhookLastReceivedAt?: string;
  webhookLastError?: string | null;
  webhookLastErrorCode?: string | null;
  webhookCallbackUrl?: string;
  webhookPdkActive?: boolean;
  webhookPdkOdometer?: number | null;
  webhookPdkEvents?: string[];
  webhookPdkScope?: string | string[] | null;
  webhookPdkSslVerification?: boolean | null;
  holderAutoSyncLicenseBlocked?: boolean;
  /** PDK custom field that holds the unit when the standard `units` array is empty. */
  unitCustomField?: string | null;
}

export interface PdkHolderCustomFieldKey {
  key: string;
  count: number;
  sample: string;
}

export interface PdkHolderCustomFields {
  fields: PdkHolderCustomFieldKey[];
  holderCount: number;
  unitCustomField: string | null;
}

export interface AcsIntegrationRow {
  id: string;
  orgId: string;
  provider: 'PDK' | 'BRIVO';
  clientId?: string | null;
  hasClientSecret: boolean;
  configJson?: AcsPdkConfigJson | null;
  status: string;
  lastError?: string | null;
  tokenExpiresAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AcsImportedDoor {
  id: string;
  orgId: string;
  acsIntegrationId: string;
  provider?: string;
  externalDoorId: string;
  displayName: string;
  metadata?: unknown;
  lastSyncedAt?: string | null;
  hasLink: boolean;
}

export interface AcsDoorLinkRow {
  id: string;
  importedDoorId: string;
  intercomDeviceId: string;
  intercomName?: string;
  isActive: boolean;
  door: {
    id: string;
    displayName: string;
    provider?: string;
    externalDoorId: string;
  };
}

export interface PdkHolderRow {
  externalHolderId: string;
  firstName: string;
  lastName: string;
  email: string;
  enabled?: boolean;
  alreadyInEntrio: boolean;
  alreadyImported: boolean;
  existingUserId?: string | null;
}

export interface PdkHolderUnit {
  id: string | null;
  name: string;
  moveIn: string | null;
  moveOut: string | null;
}

/**
 * A holder expanded via one GET per holder. Carries the unit and email that
 * PdkHolderRow (built from PDK's list endpoint) cannot provide.
 */
export interface PdkHolderDetailRow extends PdkHolderRow {
  unitName: string;
  units: PdkHolderUnit[];
  /** E.164 phone from a PDK Phone/Mobile/Cell field, when present. */
  phone?: string;
}

export interface PdkHolderDetails {
  holders: PdkHolderDetailRow[];
  failures: {externalHolderId: string; message: string}[];
}

export interface PdkReconcileCreateRow {
  holderId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
  /** E.164 phone read from PDK (a Phone/Mobile/Cell custom field), if any. */
  phone?: string | null;
  enabled?: boolean;
  defaultUnitNumber: string;
  /** Unit PDK has this holder in, or null when PDK reports none. */
  pdkUnitName?: string | null;
  pdkUnits?: PdkHolderUnit[];
  /** Where the unit came from: PDK's standard units array or the configured custom field. */
  pdkUnitSource?: 'pdk_units' | 'custom_field' | null;
  /** How the PDK unit lined up with existing Entrio rooms. */
  unitMatchStatus?:
    | 'matched'
    | 'no_unit'
    | 'unit_unmatched'
    | 'unit_ambiguous'
    | 'unit_unknown';
  /** Set when the first holder detail request failed. */
  loadError?: string | null;
  defaultApartmentId: string | null;
  defaultBuildingId: string | null;
  defaultApartmentName: string | null;
  defaultBuildingName: string | null;
  reason?: string;
}

export interface PdkReconcileUpdateRow {
  holderId: string;
  userId: string;
  pdk: {email: string | null; firstName: string | null; lastName: string | null; phone?: string | null};
  entrio: {email: string | null; firstName: string | null; lastName: string | null; phone?: string | null};
  /** Set when the PDK unit matches a different Entrio room than the tenant has now. */
  unitMove?: {
    fromBuildingName: string | null;
    fromRoomName: string | null;
    toApartmentName: string | null;
    toBuildingName: string | null;
    toRoomName: string | null;
  } | null;
}

export interface PdkReconcileDeleteRow {
  holderId: string;
  userId: string;
  email: string | null;
  firstName: string | null;
  lastName: string | null;
}

export interface PdkReconcilePreview {
  creates: PdkReconcileCreateRow[];
  updates: PdkReconcileUpdateRow[];
  deletes: PdkReconcileDeleteRow[];
  deletesDisabled: boolean;
  defaults: {
    apartmentId: string | null;
    apartmentName: string | null;
    buildingId: string | null;
    buildingName: string | null;
  };
  /** Custom field used as the unit fallback for this integration, if any. */
  unitCustomField?: string | null;
  warnings: string[];
  pdkHolderCount: number;
  entrioActiveLinkCount: number;
}

export interface PdkHolderImportRow {
  externalHolderId: string;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  unitNumber: string;
  apartmentName: string;
  buildingName: string;
  floorNumber?: string;
}

export interface PdkHolderImportResult {
  success: Array<{email?: string; [k: string]: unknown}>;
  errors: Array<{row: number; message: string; data?: unknown}>;
  totalProcessed: number;
}

export type PdkSyncStatus = 'synced' | 'ready' | 'not_imported' | 'entrio_only';

export type PdkSyncReason =
  | 'no_email'
  | 'no_unit'
  | 'unit_unmatched'
  | 'unit_ambiguous'
  | 'unit_unknown'
  | 'unit_matched'
  | 'email_in_entrio'
  | 'not_in_pdk';

export interface PdkSyncStatusRow {
  holderId: string;
  firstName: string | null;
  lastName: string | null;
  email: string | null;
  phone: string | null;
  enabled: boolean;
  pdkUnitName: string | null;
  pdkUnits: string[];
  loadError: string | null;
  status: PdkSyncStatus;
  reason: PdkSyncReason | null;
  entrio: {
    userId: string;
    email: string | null;
    phone: string | null;
    apartmentName: string | null;
    buildingName: string | null;
    roomName: string | null;
  } | null;
  lastSyncedAt: string | null;
}

export interface PdkSyncStatusReport {
  people: PdkSyncStatusRow[];
  counts: Record<PdkSyncStatus, number>;
  pdkHolderCount: number;
  autoSyncEnabled: boolean;
  unitCustomField: string | null;
  generatedAt: string;
}

export const acsIntegrationClient = {
  listIntegrations: () => apiInstance.get<{success: boolean; data: AcsIntegrationRow[]}>(base),

  connect: (body: {
    provider: 'PDK' | 'BRIVO';
    pdkSystemId?: string;
    clientId?: string;
    clientSecret?: string;
  }) => apiInstance.post<{success: boolean; data: AcsIntegrationRow}>(base, body),

  deleteIntegration: (id: string) => apiInstance.delete(`${base}/${id}`),

  setHolderAutoSync: (
    integrationId: string,
    body: {enabled: boolean; buildingId?: string; apartmentId?: string}
  ) =>
    apiInstance.post<{success: boolean; data: AcsIntegrationRow; message?: string}>(
      `${base}/${integrationId}/holder-auto-sync`,
      body
    ),

  setHolderUnitSource: (integrationId: string, body: {customField: string | null}) =>
    apiInstance.post<{success: boolean; data: AcsIntegrationRow; message?: string}>(
      `${base}/${integrationId}/holder-unit-source`,
      body
    ),

  getPdkSyncStatus: (integrationId: string) =>
    apiInstance.get<{success: boolean; data: PdkSyncStatusReport; message?: string}>(
      `${base}/${integrationId}/pdk-holders/sync-status`
    ),

  listPdkHolderCustomFields: (integrationId: string) =>
    apiInstance.get<{success: boolean; data: PdkHolderCustomFields; message?: string}>(
      `${base}/${integrationId}/pdk-holders/custom-fields`
    ),

  previewHolderReconcile: (
    integrationId: string,
    body?: {buildingId?: string; apartmentId?: string}
  ) =>
    apiInstance.post<{success: boolean; data: PdkReconcilePreview; message?: string}>(
      `${base}/${integrationId}/holder-auto-sync/preview`,
      body || {}
    ),

  applyHolderReconcile: (
    integrationId: string,
    body: {
      enabled: boolean;
      buildingId?: string;
      apartmentId?: string;
      selections?: {
        create?: Array<{
          holderId: string;
          apply?: boolean;
          apartmentId?: string;
          buildingId?: string;
          unitNumber?: string;
        }>;
        update?: Array<{holderId: string; apply?: boolean}>;
        delete?: Array<{holderId: string; apply?: boolean}>;
      };
    }
  ) =>
    apiInstance.post<{success: boolean; data: AcsIntegrationRow; message?: string}>(
      `${base}/${integrationId}/holder-auto-sync/apply`,
      body
    ),

  syncDoors: (integrationId: string) =>
    apiInstance.post<{success: boolean; data: {doors: unknown[]}}>(`${base}/${integrationId}/sync`),

  listImportedDoors: () => apiInstance.get<{success: boolean; data: AcsImportedDoor[]}>(`${base}/doors`),

  listVirtualIntercoms: () =>
    apiInstance.get<{success: boolean; data: {id: string; name: string}[]}>(
      `${base}/virtual-intercoms`
    ),

  listDoorLinks: () => apiInstance.get<{success: boolean; data: AcsDoorLinkRow[]}>(`${base}/door-links`),

  createDoorLink: (body: {importedDoorId: string; intercomDeviceId: string}) =>
    apiInstance.post(`${base}/door-links`, body),

  deleteDoorLink: (linkId: string) => apiInstance.delete(`${base}/door-links/${linkId}`),

  testDoorLink: (linkId: string) =>
    apiInstance.post<{success: boolean; data?: {provider?: string}; message?: string}>(
      `${base}/door-links/${linkId}/test`
    ),

  listPdkHolders: (integrationId: string) =>
    apiInstance.get<{success: boolean; data: PdkHolderRow[]; message?: string}>(
      `${base}/${integrationId}/pdk-holders`
    ),

  getPdkHolderDetails: (integrationId: string, holderIds: string[]) =>
    apiInstance.post<{success: boolean; data: PdkHolderDetails; message?: string}>(
      `${base}/${integrationId}/pdk-holders/details`,
      {holderIds}
    ),

  importPdkHolders: (integrationId: string, rows: PdkHolderImportRow[]) =>
    apiInstance.post<{success: boolean; data: PdkHolderImportResult; message?: string}>(
      `${base}/${integrationId}/pdk-holders/import`,
      {rows}
    ),

  listExportCandidates: (integrationId: string) =>
    apiInstance.get<{
      success: boolean;
      data: {residents: PdkExportCandidate[]; orgUsers: PdkExportCandidate[]};
      message?: string;
    }>(`${base}/${integrationId}/export-candidates`),

  exportUsersToPdk: (integrationId: string, rows: PdkExportRow[]) =>
    apiInstance.post<{success: boolean; data: PdkExportResult; message?: string}>(
      `${base}/${integrationId}/export-to-pdk`,
      {rows}
    ),
};

export interface PdkExportCandidate {
  source: 'TENANT' | 'ORG_USER';
  sourceId: string;
  entrioUserId: string | null;
  firstName: string;
  lastName: string;
  email: string;
  location?: string;
  roles?: string[];
  alreadyInPdk: boolean;
  linkSource?: 'LINK_TABLE' | 'EMAIL_MATCH' | null;
}

export interface PdkExportRow {
  source: 'TENANT' | 'ORG_USER';
  sourceId: string;
  entrioUserId: string | null;
  firstName: string;
  lastName: string;
  email: string;
}

export interface PdkExportResult {
  success: Array<{
    row: number;
    source: string;
    sourceId: string;
    email?: string;
    externalHolderId: string | null;
    linked: boolean;
  }>;
  errors: Array<{row: number; source?: string; sourceId?: string; message: string}>;
  linkSkipped: Array<{row: number; source: string; sourceId: string; email?: string; reason: string}>;
  totalProcessed: number;
}
